import React, { useState } from 'react';
import { Label } from '@atoms/index';
import { RadioButton } from '@atoms/RadioButton/RadioButton';
import { InlineErrorMessage } from '@atoms/Errors/InlineErrorMessage';
import { ErrorMessages } from '@types';
import { cn } from '@utils';

export type RadioButtonOptionsProps = {
  id?: string;
  name?: string;
  isDefault?: boolean;
  labelText: string;
  labelTextStyle?: string;
  isLabelBold?: boolean;
  subtext?: string;
  buttonRef?:
    | React.RefObject<HTMLInputElement>
    | React.MutableRefObject<HTMLInputElement>
    | null;
};

type SingleRadioButtonProps = RadioButtonOptionsProps & {
  radio: string;
  setRadio: (value: string) => void;
  onChange: (result: string) => void;
  title: string;
  checked?: boolean;
};

export interface RadioOptionsProps {
  title?: string;
  isLabelTextTitle?: boolean;
  description?: string | JSX.Element;
  className?: string;
  radioClassName?: string;
  errorPath?: string;
  errorMessages?: ErrorMessages[];
  onChange: (result: string) => void;
  options: RadioButtonOptionsProps[];
}

const SingleRadioButton: React.FC<SingleRadioButtonProps> = ({
  id,
  name,
  labelText,
  labelTextStyle,
  isLabelBold = true,
  subtext,
  buttonRef,
  radio,
  setRadio,
  onChange,
  title,
}) => {
  return (
    <div className="mb-3 flex flex-col justify-center">
      <div className="flex items-center">
        <RadioButton
          id={id}
          ref={buttonRef}
          name={name}
          value={labelText}
          checked={radio === labelText}
          onChange={() => {
            setRadio(labelText);
            onChange(labelText);
          }}
          title={title}
        />
        <div
          className={`flex flex-col justify-center pl-3 ${subtext ? 'pt-6' : ''} text-start`}
        >
          <label
            htmlFor={id}
            className={cn(
              `!text-text-body text-bodyLg mb-0 inline-block cursor-pointer select-none ${isLabelBold ? 'font-bold' : ''}`,
              labelTextStyle,
            )}
          >
            {labelText}
          </label>
          {subtext ? (
            <div className={'mt-1 max-w-lg'}>
              <pre className="body-3 max-w-lg whitespace-pre-wrap">
                {subtext}
              </pre>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export function RadioOptions(props: RadioOptionsProps) {
  const defaultText = props.options.find(
    (option) => option.isDefault,
  )?.labelText;
  const [radioText, setRadioText] = useState(defaultText || '');

  return (
    <form>
      <fieldset role="radiogroup">
        <div
          className={`mb-1 ${props.isLabelTextTitle ? 'mt-[2.75rem] flex flex-col gap-[1.5rem]' : ''}`}
        >
          <Label
            isLegend
            className={`${props.className} !text-brand-primary text-lg font-semibold`}
            id={props.errorPath ?? props.title}
            htmlFor={props.title}
            text={props.title}
            isTitle={props.isLabelTextTitle}
          />
          {props.description ? (
            <p
              data-testid="description"
              className="text-text-body text-bodyLg font-normal leading-[28.5px]"
            >
              {props.description}
            </p>
          ) : null}
          <InlineErrorMessage
            path={props.errorPath}
            messages={props.errorMessages}
          />
          <div
            className={`${props.radioClassName ? props.radioClassName : 'mt-4'} flex flex-col`}
          >
            {props.options.map((option, i) => (
              <SingleRadioButton
                key={option.labelText}
                id={option.id || option.labelText}
                title={props.title || ''}
                name={props.title}
                labelText={option.labelText}
                subtext={option.subtext}
                buttonRef={option.buttonRef}
                radio={radioText}
                setRadio={setRadioText}
                onChange={props.onChange}
                labelTextStyle={option.labelTextStyle}
                isLabelBold={option.isLabelBold}
              />
            ))}
          </div>
        </div>
      </fieldset>
    </form>
  );
}

export default RadioOptions;
